import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Phone, Users, AtSign, KeyRound, UserRound, CalendarDays, CheckCircle2, Cake, Pencil, AlertCircle, LogOut, Clock, DollarSign, CreditCard, Wallet, CalendarCheck } from 'lucide-react';
import PersonAvatar from '../../../components/PersonAvatar';
import TeacherPagos from '../components/TeacherPagos';
import TeacherNomina from '../components/TeacherNomina';
import TeacherAsistencia from '../components/TeacherAsistencia';
import { useUserDebtFlag } from '../../../context/DebtFlagsContext';
import { money } from '../../finances/api';
import BackButton from '../../../components/BackButton';
import { getTeacher, TeacherDetail, unassignTeacherClass, updateTeacher } from '../api';
import { listOperators } from '../../users/api';
import AssignClassToTeacher from '../components/AssignClassToTeacher';
import AccessModal from '../../members/components/AccessModal';
import { teacherPhotoUrl } from '../api';
import { horarioCorto } from '../../../utils/horario';
import { getWhatsAppLink } from '../../../utils/whatsapp';
import { Skeleton } from '../../../components/Skeleton';
import EsquemaPago from '../components/EsquemaPago';
import EmptyState from '../../../components/EmptyState';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { colorClase } from '../../../utils/classColor';

type Tab = 'perfil' | 'clases' | 'pagos' | 'nomina' | 'asistencia';

// Mismos estilos que la ficha de alumno: recuadro por campo (se edita al momento) y una
// tarjeta por bloque de datos, para que se note dónde termina un grupo y empieza el otro.
const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
// Fondo `canvas` (gris de página) DENTRO de la tarjeta blanca: en tema claro `surface-2` es
// casi blanco y los bloques no se distinguían («se mira muy claro» — Karla, 15-ago). En oscuro
// canvas es más oscuro que la tarjeta, así que el efecto de hueco funciona en los dos temas.
const secCls = 'rounded-xl border border-line bg-canvas px-4 pb-3.5 pt-3 shadow-inner';
// Cabecera de sección con presencia: negritas, color de texto normal (no gris), icono de marca
// y una línea que la separa de los campos (Carlos 15-ago: «el título no se nota tanto»).
const h3Cls = 'mb-2 flex items-center gap-1.5 border-b border-line pb-1.5 text-xs font-bold uppercase tracking-wider text-content';
const labelCls = 'mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-content-3';
// Datos sin los que no se puede operar: sin teléfono no se le avisa nada, sin correo no tiene
// acceso al portal, sin fecha no sale en cumpleaños.
const CLAVE = ['phone_number', 'email', 'dob'];
const vacio = (v: any) => v == null || String(v).trim() === '';

export default function TeacherDetailPage() {
  const { id } = useParams();
  const { user, hasModule } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Marketing (29-sep-2026): sin dinero → sin «Pagos y adeudos» ni chip «Debe».
  const veDinero = hasModule('letsmove_finances') || hasModule('cashbox');   // cajeras sí cobran
  const veNomina = hasModule('letsmove_payroll');                            // historial de nómina (Mabel 3-oct-2026)
  const puedeVerUsuarios = hasModule('users_admin');                       // listar cuentas (aviso «también tiene cuenta»)
  const [d, setD] = useState<TeacherDetail | null>(null);
  const [noExiste, setNoExiste] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  // ?t=asistencia: la tarjeta de la maestra en Pasar lista › Fotos abre directo su asistencia (Mabel 19-oct-2026).
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('t') === 'asistencia' ? 'asistencia' : 'perfil'));
  // Lo que debe de vitrina (fiados): misma moneda y chip que en la ficha de alumno.
  const deuda = useUserDebtFlag(id ? Number(id) : null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [campoGuardado, setCampoGuardado] = useState<string | null>(null);
  const [quitando, setQuitando] = useState<number | null>(null);   // clase esperando confirmación
  const [busyClase, setBusyClase] = useState(false);
  // Dar de baja / reactivar (Mabel 1-oct-2026: «ayer fue el último día de dos maestros y no encuentro
  // cómo ponerlos inactivos»). La opción existía escondida en Usuarios; ahora vive en la ficha.
  const [confirmBaja, setConfirmBaja] = useState(false);
  const [busyBaja, setBusyBaja] = useState(false);
  // Rol de la CUENTA: un profesor que además es gerente/cajera (Dayan) perdería TODO el acceso al darlo
  // de baja. GET /teachers/:id no trae el rol; se lee de Usuarios (si no hay permiso, queda en null).
  const [rolCuenta, setRolCuenta] = useState<string | null>(null);
  const toast = useToast();
  /** Mismo arreglo que en la ficha de alumno: cambiar de pestaña estando abajo tiraba la vista al
   *  principio. Si la barra de pestañas quedó fuera de vista, se recoloca justo arriba. */
  const tabsRef = useRef<HTMLDivElement>(null);
  const irATab = (t: Tab) => {
    const cont = tabsRef.current?.closest('main');
    const arriba = cont && tabsRef.current
      ? tabsRef.current.getBoundingClientRect().top - cont.getBoundingClientRect().top
      : 0;
    setTab(t);
    if (cont && arriba < 0) {
      const y = cont.scrollTop + arriba - 8;
      requestAnimationFrame(() => { cont.scrollTop = y; });
    }
  };
  const load = useCallback(() => {
    setNoExiste(false);
    getTeacher(Number(id))
      .then((r) => {
        setD(r.data);
        setForm({
          firstname: r.data.firstname || '', lastname: r.data.lastname || '',
          username: r.data.username || '', email: r.data.email || '',
          phone_number: r.data.phone_number || '',
          dob: r.data.dob ? String(r.data.dob).slice(0, 10) : '',
          notes: r.data.notes || '',
        });
      })
      .catch(() => { setD(null); setNoExiste(true); });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Solo quien administra usuarios puede listarlos: Dayan y Aracely (gerentes sin users_admin)
    // se llevaban un 403 cada vez que abrían una ficha de maestro (7-oct-2026). Sin ese permiso
    // simplemente no sale el aviso de «también tiene cuenta de…».
    if (!isAdmin || !puedeVerUsuarios) return;
    listOperators().then((r) => setRolCuenta(r.users.find((u) => u.id === Number(id))?.role ?? null)).catch(() => {});
  }, [id, isAdmin, puedeVerUsuarios]);

  async function cambiarActivo(nuevo: boolean) {
    setBusyBaja(true);
    try {
      await updateTeacher(Number(id), { active: nuevo ? 1 : 0 });
      setConfirmBaja(false);
      toast.success(nuevo ? 'Profesor reactivado' : 'Profesor dado de baja');
      load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo cambiar el estado'); }
    finally { setBusyBaja(false); }
  }

  /** La confirmación se pide en la propia tarjeta (ya no con el `confirm()` del navegador).
   *  El backend deja `teacher_leader_id = NULL`: la clase sigue viva, pero SIN profesor. */
  async function quitarClase(classId: number, nombre: string) {
    setBusyClase(true);
    try {
      await unassignTeacherClass(Number(id), classId);
      setQuitando(null);
      toast.success(`${nombre} se quedó sin profesor`);
      load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
    finally { setBusyClase(false); }
  }

  // Sin esto, un 404 (p. ej. alguien del equipo que no da clases) dejaba el esqueleto cargando para siempre.
  if (!d && noExiste) return (
    <div className="rounded-2xl border border-line bg-surface p-6 text-center">
      <p className="text-sm font-medium text-content">Esta persona no tiene ficha de profesor.</p>
      <Link to="/profesores" className="mt-2 inline-block text-xs font-medium text-brand-blue hover:underline">← Volver a Profesores</Link>
    </div>
  );
  if (!d) return <Skeleton className="h-40 w-full rounded-2xl" />;

  // STAFF que no da clases (Mabel 3-oct-2026): misma ficha, sin pestaña de clases ni esquema de pago;
  // el subtítulo dice su puesto (Cajera / Gerente / Dueño(a)) y «Volver» cae en la pestaña Staff.
  const esProfe = d.es_profesor !== 0;
  const queEs = esProfe ? 'Profesor' : (d.puesto || 'Staff');

  /** Valor GUARDADO hoy, en el formato del input, para saber si de verdad cambió algo. */
  function valorGuardado(key: string) {
    if (!d) return '';
    if (key === 'dob') return d.dob ? String(d.dob).slice(0, 10) : '';
    const v = (d as any)[key];
    return v == null ? '' : String(v);
  }

  /** Guarda AL SALIR del campo y sólo si cambió — mismo patrón que la ficha de alumno (Carlos
   *  15-ago: «replica lo de alumnos, es el medio más sencillo»). Sin botón Guardar.
   *  `name` del backend se arma con firstname+lastname, así que esos dos viajan siempre juntos. */
  async function guardarCampo(key: string) {
    const actual = form[key] == null ? '' : String(form[key]);
    if (actual === valorGuardado(key)) return;
    if (key === 'username' && !actual.trim()) {   // el backend rechaza usuario vacío (400)
      setForm({ ...form, username: valorGuardado('username') });
      toast.error('El usuario no puede quedar vacío');
      return;
    }
    const patch: Record<string, any> =
      (key === 'firstname' || key === 'lastname')
        ? { firstname: String(form.firstname || '').trim(), lastname: String(form.lastname || '').trim() }
        : { [key]: key === 'dob' ? (actual || null) : actual };
    // Si se captura teléfono y no hay prefijo, se asume 52 (México) — igual que el alta.
    if (key === 'phone_number' && actual) patch.phone_prefix = d?.phone_prefix || '52';
    setSaving(true);
    setCampoGuardado(key);
    try {
      await updateTeacher(Number(id), patch);
      // Refresca sólo `d` (lo guardado); NO se toca `form`: la respuesta llega cuando ya se está
      // tecleando en el siguiente recuadro y borraría lo escrito.
      getTeacher(Number(id)).then((r) => setD(r.data)).catch(() => {});
      setTimeout(() => setCampoGuardado((k) => (k === key ? null : k)), 1800);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar');
      setForm({ ...form, [key]: valorGuardado(key) });
      setCampoGuardado(null);
    } finally { setSaving(false); }
  }

  /** Un campo editable (recuadro que se guarda al salir), o texto plano si no eres admin.
   *  Los datos que hacen falta para operar (teléfono, correo, fecha) se marcan en ámbar, igual que
   *  en la ficha de alumno (Carlos 15-ago: «el ámbar no lo pusiste en el correo del profesor»). */
  const campo = (key: string, label: string, Icono: typeof UserRound, type = 'text') => {
    const ok = campoGuardado === key;
    const falta = CLAVE.includes(key) && vacio(form[key]);
    return (
      <div>
        <label htmlFor={`t-${key}`} className={labelCls}>
          <Icono size={13} className="shrink-0" /> {label}
          {falta && <span className="inline-flex items-center gap-0.5 normal-case text-amber-600 dark:text-amber-400"><AlertCircle size={12} /> falta</span>}
          {ok && <CheckCircle2 size={13} className="text-emerald-500" aria-label="Guardado" />}
        </label>
        {isAdmin ? (
          <input id={`t-${key}`} type={type} disabled={saving}
            value={form[key] ?? ''}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            onBlur={() => guardarCampo(key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') { setForm({ ...form, [key]: valorGuardado(key) }); e.currentTarget.blur(); }
            }}
            className={`${inputCls} ${ok ? 'border-emerald-500/60' : falta ? 'border-amber-500/50 bg-amber-500/[0.04]' : ''}`} />
        ) : (
          <div className="text-sm text-content">{valorGuardado(key) || '—'}</div>
        )}
      </div>
    );
  };

  const phone = `${d.phone_prefix || ''}${d.phone_number || ''}`.trim();
  const wa = getWhatsAppLink(d.phone_prefix, d.phone_number, `Hola ${d.firstname} 👋`);
  // Alumnos ÚNICOS (mismo criterio que la lista) — la suma por clase duplica a quien toma varias.
  const totalStudents = d.students ?? d.classes.reduce((a, c) => a + c.enrolled, 0);
  const faltantes = [
    [vacio(form.phone_number), 'teléfono'], [vacio(form.email), 'correo'], [vacio(form.dob), 'fecha de nacimiento'],
  ].filter(([f]) => f).map(([, t]) => t as string);

  const tabs: { key: Tab; label: string; icon: typeof UserRound }[] = [
    { key: 'perfil', label: 'Perfil', icon: UserRound },
    ...(esProfe ? [{ key: 'clases' as Tab, label: 'Clases', icon: CalendarDays }] : []),
    ...(veDinero ? [{ key: 'pagos' as Tab, label: 'Pagos y adeudos', icon: CreditCard }] : []),
    ...(veNomina ? [{ key: 'nomina' as Tab, label: 'Nómina', icon: Wallet }] : []),
    // Asistencia (Mabel 19-oct-2026): maestros = sus clases del horario + faltas/permisos/retardos; staff = sus
    // faltas/permisos/retardos, que desde el 19-oct se REGISTRAN en el perfil (sin montos): la ve quien ve la ficha.
    { key: 'asistencia' as Tab, label: 'Asistencia', icon: CalendarCheck },
  ];

  return (
    // Mismo patrón que la ficha de alumno v2 (28-jul, Jesara): UNA banda de cabecera compacta
    // (foto + nombre + chips + acciones) con las pestañas pegadas debajo — sin partir en columnas.
    // El aire entre la barra superior y la ficha se recorta a la mitad en escritorio (Carlos
    // 15-ago: «queda mucho espacio entre el breadcrumb y el perfil»): el «Volver» ya vive en la
    // barra, así que ese margen sólo separaba dos cosas que se leen juntas.
    <div className="md:-mt-3">
      <BackButton fallback={esProfe ? '/profesores' : '/profesores?tab=staff'} fallbackLabel={esProfe ? 'profesores' : 'staff'} />

      {/* ===== Cabecera compacta + pestañas ===== */}
      <div className="rounded-2xl bg-surface shadow-sm">
        {/* Retrato al MISMO tamaño que la ficha de alumno (200 px / 128 px en móvil, círculo) —
            Carlos 15-ago: «ajusta el avatar del maestro igual que el que nos ha molado». */}
        <div className="flex flex-col items-center gap-4 p-5 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left">
          {/* PersonAvatar (no <img> suelto): así la moneda de «debe» le sigue también al maestro
              (Carlos 29-sep). `userId` y sin contactId: su adeudo vive en users, no en contacts. */}
          <PersonAvatar userId={d.account_id} hasPhoto={d.has_photo} photoSrc={teacherPhotoUrl(d.account_id)}
            name={d.firstname} lastName={d.lastname} size="h-32 w-32 sm:h-[200px] sm:w-[200px]"
            initialClass="text-5xl sm:text-7xl" className="lm-avatar--xl ring-1 ring-line rounded-full" ringClass="fill-surface"
            badgeCorner="tr" />

          <div className="min-w-0 flex-1">
            {/* Tres renglones, como la ficha de alumno (Carlos 15-ago): nombre + rol arriba, la
                carga de trabajo debajo, y al final los chips de contacto/avisos. */}
            <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-0.5 sm:justify-start">
              <h1 className="font-display text-2xl font-bold leading-tight text-content sm:text-3xl">{d.firstname} {d.lastname}</h1>
              <span className="text-sm text-content-2" data-testid="ficha-que-es">
                {queEs}
                {!d.active && <> · <span className="text-content-3">inactivo</span></>}
              </span>
            </div>
            {esProfe ? (
              <div className="mt-0.5 text-sm text-content-2">
                <span className="font-semibold text-accent-ink">{d.classes.length} clases</span>
                <span className="mx-1.5 text-content-3">·</span>
                <span className="font-semibold text-accent-ink">{totalStudents} alumnos</span>
              </div>
            ) : (
              <div className="mt-0.5 text-sm text-content-3">Staff del estudio · no da clases</div>
            )}
            <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              {/* El estado de acceso y el usuario ya NO viven aquí (Carlos 15-ago): el usuario
                  está en su campo del perfil y «Generar acceso» bajó al final de la pestaña,
                  igual que en la ficha de alumno. Arriba sólo lo que se consulta a diario. */}
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs font-medium text-emerald-600 transition hover:bg-surface-2 dark:text-emerald-400">
                  <Phone size={13} /> WhatsApp
                </a>
              )}
              {d.email && (
                <a href={`mailto:${d.email}`}
                  className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-xs font-medium text-content-2 transition hover:bg-surface-2">
                  <Mail size={13} /> {d.email}
                </a>
              )}
              {/* Debe de vitrina: chip rojo como en la ficha de alumno; clic → pestaña de pagos. */}
              {veDinero && deuda && deuda.total > 0 && (
                <button onClick={() => irATab('pagos')} data-testid="profe-chip-debe"
                  className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2.5 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-500/25 dark:text-red-400">
                  <AlertCircle size={13} /> Debe {money(deuda.total)}
                </button>
              )}
              {/* Resumen de lo que falta, visible desde cualquier pestaña (igual que en alumnos) */}
              {faltantes.length > 0 && (
                <span title={`Faltan: ${faltantes.join(', ')}`}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                  <AlertCircle size={13} /> Falta{faltantes.length === 1 ? '' : 'n'} {faltantes.length} dato{faltantes.length === 1 ? '' : 's'}
                </span>
              )}
            </div>

            {/* Los chips de «última clase» y «siguiente» se retiraron el 15-ago a petición de
                Carlos: la cabecera se lee más limpia sin ellos. El backend (GET /teachers/:id)
                sigue devolviendo `last_class` y `next_class` por si se retoman. */}
          </div>

        </div>

        {/* Pestañas pegadas a la cabecera */}
        <div ref={tabsRef} className={`grid ${tabs.length === 5 ? 'grid-cols-5' : tabs.length === 4 ? 'grid-cols-4' : tabs.length === 3 ? 'grid-cols-3' : tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-1 border-t border-line px-2 sm:flex sm:px-3`}>
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => irATab(key)}
              className={`flex flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-[11px] font-medium leading-tight transition sm:flex-row sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm ${
                tab === key ? 'border-accent text-accent-ink' : 'border-transparent text-content-2 hover:text-content'
              }`}>
              <Icon size={16} className="shrink-0" /> <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== PERFIL — edición directa, sin botón Guardar (igual que alumnos) ===== */}
      {tab === 'perfil' && (
        <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm">
          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
            <section className={`${secCls} xl:col-span-2`}>
              <h3 className={h3Cls}><UserRound size={13} className="text-accent-ink" /> Datos personales</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {campo('firstname', 'Nombre', UserRound)}
                {campo('lastname', 'Apellidos', UserRound)}
                {campo('dob', 'Fecha de nacimiento', Cake, 'date')}
              </div>
            </section>
            <section className={secCls}>
              <h3 className={h3Cls}><Phone size={13} className="text-accent-ink" /> Contacto</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {campo('phone_number', 'Teléfono', Phone, 'tel')}
                {campo('email', 'Correo', Mail, 'email')}
              </div>
            </section>
            <section className={secCls}>
              <h3 className={h3Cls}><KeyRound size={13} className="text-accent-ink" /> Acceso</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {campo('username', 'Usuario', AtSign)}
                <div>
                  <label className={labelCls}><CheckCircle2 size={13} className="shrink-0" /> Estado</label>
                  <div className="pt-2 text-sm text-content">{d.active ? 'Activo' : 'Inactivo'}</div>
                </div>
              </div>
            </section>
            <section className={`${secCls} xl:col-span-2`}>
              <h3 className={h3Cls}><Pencil size={13} className="text-accent-ink" /> Comentarios</h3>
              {isAdmin ? (
                <textarea id="t-notes" rows={4} disabled={saving}
                  value={form.notes ?? ''}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  onBlur={() => guardarCampo('notes')}
                  className={`${inputCls} ${campoGuardado === 'notes' ? 'border-emerald-500/60' : ''}`} />
              ) : (
                <div className="whitespace-pre-wrap text-sm text-content">{d.notes || '—'}</div>
              )}
            </section>
          </div>
          {/* Acceso al portal al FINAL, como en la ficha de alumno: se hace una vez en la vida
              del profesor, no en cada visita. Aquí se ve además si ya tiene contraseña. */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
            <div className="text-xs text-content-3">
              {d.has_password
                ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={13} /> Ya tiene acceso {esProfe ? 'al portal' : 'al sistema'}</span>
                : <span className="inline-flex items-center gap-1"><KeyRound size={13} /> Todavía no tiene acceso {esProfe ? 'al portal' : 'al sistema'}</span>}
              <span className="ml-1">
                {esProfe
                  ? <>— {d.firstname} ve ahí sus clases y su lista de alumnos.</>
                  : <>— su puesto y permisos se manejan en{' '}
                      {hasModule('users_admin') ? <Link to="/usuarios" className="font-medium text-accent-ink hover:underline">Usuarios</Link> : 'Usuarios'}.</>}
              </span>
            </div>
            {/* El acceso del staff nace con la invitación de Usuarios (puesto + correo); aquí solo el del profesor. */}
            {esProfe && (
              <button onClick={() => setAccessOpen(true)}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                <KeyRound size={15} /> Generar acceso
              </button>
            )}
          </div>
          {/* ===== Dar de baja / Reactivar (Mabel 1-oct-2026) — mismo lugar y tono que en la ficha de alumno ===== */}
          {isAdmin && (
            <div className="mt-4 space-y-3 border-t border-line pt-3" data-testid="profe-estado">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-content">
                    {d.active
                      ? <><CheckCircle2 size={15} className="shrink-0 text-emerald-500" /> {esProfe ? 'Profesor activo' : 'En el equipo'}</>
                      : <><AlertCircle size={15} className="shrink-0 text-content-3" /> Dado de baja</>}
                  </div>
                  <div className="mt-0.5 text-xs text-content-3">
                    {d.active
                      ? (esProfe ? 'Sale en Profesores y puede entrar a su portal.' : 'Sale en Staff y puede entrar al sistema con su puesto.')
                      : `Ya no sale en ${esProfe ? 'Profesores' : 'Staff'} ni puede entrar al sistema. Su historial (${esProfe ? 'clases, ' : ''}pagos, nómina) se conserva.`}
                  </div>
                </div>
                {!confirmBaja && (
                  <button onClick={() => (d.active ? setConfirmBaja(true) : cambiarActivo(true))} disabled={busyBaja}
                    data-testid="profe-btn-estado"
                    className={`shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                      d.active
                        ? 'border-line bg-surface text-content-2 hover:border-red-500/50 hover:text-red-600 dark:hover:text-red-400'
                        : 'border-emerald-500/40 bg-surface text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400'}`}>
                    {d.active ? 'Dar de baja' : 'Reactivar'}
                  </button>
                )}
              </div>
              {confirmBaja && d.active ? (
                <div className="space-y-2 rounded-xl border border-red-500/30 bg-red-500/[0.04] p-3 text-sm" data-testid="profe-confirm-baja">
                  <div className="font-medium text-content">¿Dar de baja a {d.firstname}?</div>
                  {d.classes.length > 0 && (
                    <div className="text-content-2" data-testid="profe-aviso-clases">
                      <AlertCircle size={13} className="mr-1 inline text-amber-500" />
                      Todavía tiene <b>{d.classes.length} {d.classes.length === 1 ? 'clase activa' : 'clases activas'}</b>
                      {' '}({d.classes.slice(0, 4).map((c) => c.name).join(', ')}{d.classes.length > 4 ? '…' : ''}). Seguirán a su nombre:
                      {' '}lo ideal es pasárselas a otro profesor o quitárselas en la pestaña{' '}
                      <button type="button" onClick={() => { setConfirmBaja(false); irATab('clases'); }} className="font-medium text-accent-ink underline">Clases</button>.
                    </div>
                  )}
                  {esProfe && rolCuenta && rolCuenta !== 'staff' && (
                    <div className="text-content-2" data-testid="profe-aviso-rol">
                      <AlertCircle size={13} className="mr-1 inline text-red-500" />
                      Ojo: {d.firstname} también tiene cuenta de <b>{rolCuenta === 'owner' ? 'dueño' : 'gerente / caja'}</b>. Darlo de baja
                      le quita TODO el acceso al sistema, no solo las clases. Si solo deja de dar clases, quítale sus clases y no lo des de baja.
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={() => cambiarActivo(false)} disabled={busyBaja} data-testid="profe-btn-confirmar-baja"
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-40">
                      {d.classes.length > 0 ? 'Dar de baja de todos modos' : 'Sí, dar de baja'}
                    </button>
                    <button onClick={() => setConfirmBaja(false)} disabled={busyBaja}
                      className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          {isAdmin && <p className="mt-3 text-xs text-content-3">Los cambios se guardan solos al salir de cada recuadro. Escape deshace lo que acabas de escribir.</p>}
          {!isAdmin && phone && <p className="mt-3 text-xs text-content-3">Teléfono: {phone}</p>}
        </div>
      )}

      {/* ===== CLASES ===== */}
      {tab === 'clases' && esProfe && (
        <div className="mt-3 rounded-2xl bg-surface p-4 shadow-sm">
          {/* Título + acción en la misma fila y arriba del todo, igual que en la ficha de alumno */}
          <div className="mb-3 flex items-center justify-between gap-2 border-b border-line pb-1.5">
            <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content">
              <Users size={13} className="text-accent-ink" /> Sus clases ({d.classes.length})
            </h2>
            {/* Asignar desde aquí, sin entrar clase por clase (video Karla 30-jul 19:57) */}
            <AssignClassToTeacher teacherId={d.account_id} ownClassIds={d.classes.map((c) => c.class_id)} onAssigned={load} />
          </div>

          {/* CÓMO COBRA: una sola pieza que entiende los tres esquemas (por hora, por hora con
              tarifa de mañana, comisión). Antes esto era un input de «Precio por hora» a secas,
              que a Isabel le escondía su tarifa de mañana y a Noli le enseñaba «$0 / hora». */}
          {isAdmin && (
            <EsquemaPago
              userId={d.account_id}
              valor={{ pay_mode: d.pay_mode, hourly_rate: d.hourly_rate, morning_rate: d.morning_rate,
                       morning_until: d.morning_until, commission_pct: d.commission_pct }}
              onSaved={load}
            />
          )}

          {d.classes.length === 0 ? (
            <EmptyState icon={Users} title="Sin clases asignadas" compact
              subtitle={isAdmin ? 'Usa el botón «Asignar clase».' : undefined} />
          ) : (
            // Bloque completo por clase (Carlos 15-ago), con el color sólo en la franja izquierda,
            // cupo y horario — nada de repetir el estilo, que suele llamarse igual que la clase.
            <div className="space-y-2.5">
              {d.classes.map((c) => (
                <div key={c.class_id}
                  className="relative overflow-hidden rounded-xl border border-line bg-canvas shadow-sm transition hover:shadow-md">
                  <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: colorClase(c.color) }} />
                  <div className="relative flex items-start gap-3 py-3 pl-4 pr-2">
                    <div className="min-w-0 flex-1">
                      <Link to={`/clases/${c.class_id}`}
                        className="block truncate text-sm font-semibold text-content underline-offset-2 hover:underline">
                        {c.name}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                        <span title="Inscritos / cupo"
                          className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 font-medium text-content-2 ring-1 ring-line">
                          <Users size={11} /> {c.enrolled}{c.capacity ? `/${c.capacity}` : ''}
                          {!!c.capacity && Number(c.enrolled) >= Number(c.capacity) && <b className="text-amber-600 dark:text-amber-400">lleno</b>}
                        </span>
                        {horarioCorto(c.schedule) && (
                          <span className="inline-flex items-center gap-1 text-content-3">
                            <Clock size={11} className="shrink-0" /> {horarioCorto(c.schedule)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Quitársela: la clase NO se borra, se queda «Sin profesor» y sale en el hueco
                        de clases sin maestro. Pregunta antes, igual que la baja del alumno. */}
                    {isAdmin && (quitando === c.class_id ? (
                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className="hidden text-[11px] font-medium text-content-2 sm:inline">¿Quitársela?</span>
                        <button onClick={() => quitarClase(c.class_id, c.name)} disabled={busyClase}
                          title={`${c.name} se quedará sin profesor`}
                          className="rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-red-700 disabled:opacity-40">
                          Sí, quitársela
                        </button>
                        <button onClick={() => setQuitando(null)} disabled={busyClase}
                          className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-content-2 transition hover:bg-surface-2">
                          No
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setQuitando(c.class_id)}
                        className="shrink-0 rounded-lg border border-line p-1.5 text-content-3 transition hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                        title="Quitarle esta clase (se queda sin profesor)" aria-label="Quitarle esta clase">
                        <LogOut size={15} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== PAGOS Y ADEUDOS (fiados de vitrina) ===== */}
      {tab === 'pagos' && veDinero && <TeacherPagos userId={d.account_id} isAdmin={isAdmin} />}

      {/* ===== NÓMINA (historial de corridas, bonos/comisiones y entregas) ===== */}
      {tab === 'nomina' && veNomina && <TeacherNomina userId={d.account_id} />}

      {/* ===== ASISTENCIA (clases del horario / faltas-permisos-retardos del staff) ===== */}
      {tab === 'asistencia' && <TeacherAsistencia userId={d.account_id} esProfe={esProfe} />}

      {accessOpen && <AccessModal accountId={d.account_id} onClose={() => setAccessOpen(false)} />}
    </div>
  );
}
